import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, KeyRound, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm } from "../../lib/permissions";
import { PageHead, Empty, ago } from "./common";

const STATUS_STYLE = {
  Active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "API key required": "bg-amber-100 text-amber-800 border-amber-200",
  Disabled: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function DataSources() {
  const { user } = useAuth();
  const canSync = hasPerm(user, "sea_freight", "create");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/sea-freight/data-sources").then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (src) => {
    try {
      await api.put(`/sea-freight/data-sources/${src.key}`, { enabled: !src.enabled, note: src.note || "" });
      toast.success(`${src.name} ${!src.enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/sea-freight/sync");
      toast.success(`${r.news.added} new article(s) · currency ${r.currency.as_of || "unchanged"}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const groups = (data?.sources || []).reduce((acc, s) => { (acc[s.category] = acc[s.category] || []).push(s); return acc; }, {});

  return (
    <div data-testid="data-sources-page">
      <PageHead title="Data Sources & API Management"
        subtitle="Every integration is a separate adapter. API keys live only in backend environment variables — they are never sent to the browser. Sources without a key stay disabled and are shown as such."
        right={canSync && <Button data-testid="manual-sync-btn" variant="outline" className="rounded-sm gap-1.5" disabled={busy} onClick={sync}>
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh supported sources
        </Button>} />

      {data && !data.can_manage && (
        <div className="bg-slate-50 border border-slate-200 text-slate-600 rounded-sm px-4 py-2.5 text-sm mb-4" data-testid="sources-readonly-note">
          You can view source status. Enabling or disabling a source requires the "Sea Freight — Data Sources & API Keys" permission.
        </div>
      )}

      <div className="text-xs text-slate-500 mb-3" data-testid="last-sync-note">Last successful synchronization: {ago(data?.last_successful_sync)}</div>

      <div className="space-y-4">
        {Object.entries(groups).map(([cat, rows]) => (
          <section key={cat} className="bg-white border border-slate-200 rounded-sm">
            <header className="px-4 py-3 border-b border-slate-200"><h2 className="text-base font-semibold text-slate-900 font-head">{cat}</h2></header>
            <div className="divide-y divide-slate-100">
              {rows.map((s) => (
                <div key={s.key} data-testid={`source-${s.key}`} className="px-4 py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">{s.name}</span>
                      <Badge variant="outline" className={`rounded-full px-2 text-[11px] ${STATUS_STYLE[s.effective_status]}`} data-testid={`source-status-${s.key}`}>{s.effective_status}</Badge>
                      {s.key_required && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                          <KeyRound className="w-3 h-3" /> {s.env_key}
                          {s.key_present ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <XCircle className="w-3 h-3 text-slate-400" />}
                        </span>
                      )}
                    </div>
                    {s.note && <p className="text-xs text-slate-500 mt-0.5">{s.note}</p>}
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {s.url ? <a href={s.url} target="_blank" rel="noreferrer" className="hover:text-slate-700 underline">{s.url}</a> : "No endpoint"}
                      {" · "}last sync {ago(s.last_sync_at)}
                      {s.last_status === "failed" && <span className="text-red-600"> · last run failed: {s.last_error}</span>}
                    </div>
                  </div>
                  <div className="shrink-0 pt-1">
                    <Switch data-testid={`toggle-source-${s.key}`} checked={!!s.enabled} disabled={!data?.can_manage} onCheckedChange={() => toggle(s)} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <section className="bg-white border border-slate-200 rounded-sm mt-4">
        <header className="px-4 py-3 border-b border-slate-200"><h2 className="text-base font-semibold text-slate-900 font-head">Synchronization log</h2></header>
        {(data?.recent_logs || []).length === 0 ? <div className="p-4"><Empty testid="sync-logs-empty">No synchronization runs recorded yet.</Empty></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>{["When", "Source", "Status", "Fetched", "Added", "Error"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>))}</tr>
              </thead>
              <tbody>
                {data.recent_logs.map((l) => (
                  <tr key={l.id} data-testid={`sync-log-${l.id}`} className="border-b border-slate-100">
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{ago(l.at)}</td>
                    <td className="px-3 py-2 text-slate-700">{l.source_key}</td>
                    <td className="px-3 py-2">
                      {l.status === "success"
                        ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> success</span>
                        : <span className="text-red-600 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> failed</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{l.records_fetched}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{l.records_added}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 max-w-[320px] truncate">{l.error || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
